import { TEXT_ALIGNMENT, WIDGET_DEFAULT_FONT } from 'constants/fonts';
import { Bold, Italic, Underline } from 'modules/common/components/FontStyle';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { memo, useCallback, useMemo, useRef } from 'react';
import { VerticalAlignment } from 'widgets/TextBasedWidgets/TableWidget/toolbar/VerticalAlignment';
import { FontFamily, FontSize, LineHeight, TextAlignment, TextColor } from 'widgets/TextBasedWidgets/common/toolbar';
import { useWidget } from 'widgets/sdk';

import { LABEL_ALIGNMENT_OPTIONS } from '../FormWidget.config';
import {
  FontStyleOption,
  FontWeightOption,
  FormWidgetData,
  LabelAlignment,
  TextDecorationOption,
} from '../FormWidget.types';

const MAX_LINE_HEIGHT = 10;

export const LabelTextToolbar = () => {
  const {
    style: {
      fontFamily = WIDGET_DEFAULT_FONT,
      fontSize,
      fontWeight,
      fontStyle,
      textDecoration,
      textAlign,
      lineHeight,
      alignSelf,
    },
    labelPosition,
    updateWidget,
  } = useWidget<FormWidgetData>();

  const isVerticalLayout = labelPosition === 'top';
  const isHorizontalLayout = labelPosition === 'left';
  const isBold = fontWeight === 'bold';
  const isItalic = fontStyle === 'italic';
  const isUnderline = textDecoration === 'underline';

  const onSetFontFamily = useCallback(
    (fontFamily: string) => {
      if (!fontFamily) return;
      updateWidget({ style: { fontFamily } });
    },
    [updateWidget],
  );

  const onSetFontSize = useCallback(
    (newFontSize: number) => {
      if (!newFontSize) return;

      updateWidget({ style: { fontSize: newFontSize } });
    },
    [updateWidget],
  );

  const onIncrementFontSize = useCallback(() => {
    updateWidget({ style: { fontSize: fontSize + 1 } });
  }, [fontSize, updateWidget]);

  const onDecrementFontSize = useCallback(() => {
    updateWidget({ style: { fontSize: fontSize - 1 } });
  }, [fontSize, updateWidget]);

  const onSetBold = useCallback(() => {
    updateWidget({ style: { fontWeight: isBold ? FontWeightOption.Normal : FontWeightOption.Bold } });
  }, [isBold, updateWidget]);

  const onSetItalic = useCallback(() => {
    updateWidget({ style: { fontStyle: isItalic ? FontStyleOption.Normal : FontStyleOption.Italic } });
  }, [isItalic, updateWidget]);

  const onSetUnderline = useCallback(() => {
    updateWidget({
      style: { textDecoration: isUnderline ? TextDecorationOption.None : TextDecorationOption.Underline },
    });
  }, [isUnderline, updateWidget]);

  const onSetTextAlign = useCallback(
    (alignment: TEXT_ALIGNMENT) => {
      if (!alignment) return;
      updateWidget({ style: { textAlign: alignment } });
    },
    [updateWidget],
  );

  const onSetVerticalAlignment = useCallback(
    (verticalAlignment: string) => {
      const alignSelf = verticalAlignment as LabelAlignment;
      updateWidget({ style: { alignSelf } });
    },
    [updateWidget],
  );

  const onSetLineHeight = useCallback(
    (lineHeight: string) => {
      updateWidget({ style: { lineHeight } });
    },
    [updateWidget],
  );

  return (
    <>
      <FontColor />
      <FontFamily value={fontFamily} onChange={onSetFontFamily} />
      <FontSize
        value={fontSize}
        onChange={onSetFontSize}
        onIncrement={onIncrementFontSize}
        onDecrement={onDecrementFontSize}
      />
      <ToolbarDivider />
      <Bold fontFamily={fontFamily} isActive={isBold} onChange={onSetBold} />
      <Italic fontFamily={fontFamily} isActive={isItalic} onChange={onSetItalic} />
      <Underline isActive={isUnderline} onChange={onSetUnderline} />
      <ToolbarDivider />
      {isVerticalLayout && <TextAlignment value={textAlign ?? TEXT_ALIGNMENT.left} onChange={onSetTextAlign} />}
      {isHorizontalLayout && (
        <VerticalAlignment
          value={alignSelf}
          onChange={onSetVerticalAlignment}
          customOptions={LABEL_ALIGNMENT_OPTIONS}
        />
      )}
      <LineHeight value={lineHeight} onChange={onSetLineHeight} maxLineHeight={MAX_LINE_HEIGHT} />
    </>
  );
};

const FontColor = memo(() => {
  const {
    style: { color, fontFamily, fontSize, fontWeight },
    updateWidget,
  } = useWidget<FormWidgetData>();

  const isBold = fontWeight === 'bold';
  const widgetElRef = useRef<HTMLElement | null>();
  const textStyleForColorChecker = useMemo(
    () => ({
      fontFamily: fontFamily ?? '',
      fontSize: fontSize.toString(),
      fontWeight: isBold ? 'bold' : 'normal',
    }),
    [fontFamily, fontSize, isBold],
  );

  const targetRectForColorChecker = widgetElRef.current?.getBoundingClientRect();

  const onSetFontColor = useCallback(
    (color: string) => {
      updateWidget({ style: { color } });
    },
    [updateWidget],
  );

  return (
    <TextColor
      color={color ?? ''}
      onChange={onSetFontColor}
      colorCheckerProps={{
        color: color ?? '',
        textStyle: textStyleForColorChecker,
        targetRect: targetRectForColorChecker,
      }}
    />
  );
});
FontColor.displayName = 'FontColor';
